import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { ObjHlp } from 'src/lib/fliko/helpers/object.helper';
import { ReviewContractorData } from 'src/lib/fliko/models/estate/contractor.data';
import { EstateReviewSettings, ReviewData, ReviewReportData } from 'src/lib/fliko/models/estate/review.data';
import { ReviewHistoryRecordData } from 'src/lib/fliko/models/estate/review-history.data';
import { Collection } from 'src/lib/fliko/types/database.type';
import { Nullish } from 'src/lib/fliko/types/typeish.type';
import { DatabaseService } from 'src/app/shared/services/database.service';

@Injectable({ providedIn: 'root' })
export class ReviewsService {
  constructor(private dbSrv: DatabaseService) {}

  // #region Review
  /**
   * @path /review/$estateId/
   * @key id
   */
  private get review() {
    return this.dbSrv.estateFactory.getCollection<ReviewData>('review/$eId');
  }

  public getReviews$(): Observable<ReviewData[]> {
    return this.review.getList();
  }

  public getReview$(id: string): Observable<ReviewData | Nullish> {
    return this.review.getElement(id);
  }

  public setReview(data: ReviewData): Promise<void> {
    return this.review.setElement(data.id, data);
  }

  public updateReview(data: ReviewData): Promise<void> {
    return this.review.updateElement(data.id, data);
  }

  public removeReview$(id: string, permanently: boolean): Promise<void> {
    return permanently ? this.review.removeElement(id) : this.review.updateElement(id, { deleted: Date.now() });
  }

  public restoreReview$(id: string): Promise<void> {
    return this.review.updateElement(id, { deleted: null });
  }
  // #endregion Review

  // #region Report
  /**
   * @param reviewId
   * @path /reviewReport/$estateId/$reviewId/
   * @key id
   */
  private reviewReport = (reviewId: string) =>
    this.dbSrv.estateFactory.getCollection<ReviewReportData>(`reviewReport/$eId/${reviewId}`);

  /**
   * @path /reviewReport/$eId/
   * @key reviewId
   */
  private allReports = this.dbSrv.estateFactory.getCollectionReadOnly<Collection<ReviewReportData>>('reviewReport/$eId');

  public getAllReports$(): Observable<ReviewReportData[]> {
    return this.allReports.getObject().pipe(map((obj) => ObjHlp.flatten<ReviewReportData>(obj)));
  }

  public getReports$(reviewId: string): Observable<ReviewReportData[]> {
    return this.reviewReport(reviewId).getList();
  }

  public getReport$(reviewId: string, id: string): Observable<ReviewReportData | Nullish> {
    return this.reviewReport(reviewId).getElement(id);
  }

  public setReport(data: ReviewReportData): Promise<void> {
    return this.reviewReport(data.reviewId).setElement(data.id, data);
  }

  public updateReport(data: ReviewReportData): Promise<void> {
    return this.reviewReport(data.reviewId).updateElement(data.id, data);
  }

  public removeReport$(reviewId: string, reportId: string, permanently: boolean): Promise<void> {
    const reportRef = this.reviewReport(reviewId);
    return permanently ? reportRef.removeElement(reportId) : reportRef.updateElement(reportId, { deleted: Date.now() });
  }

  public restoreReport$(reviewId: string, reportId: string): Promise<void> {
    return this.reviewReport(reviewId).updateElement(reportId, { deleted: null });
  }

  // #region Contractor
  /**
   * @path /reviewContractor/$companyId/
   * @key id
   */
  private get reviewContractor() {
    return this.dbSrv.companyFactory.getCollection<ReviewContractorData>('reviewContractor/$cId');
  }

  public getContractors$(): Observable<ReviewContractorData[]> {
    return this.reviewContractor.getList();
  }

  public getContractor$(id: string): Observable<ReviewContractorData | Nullish> {
    return this.reviewContractor.getElement(id);
  }

  public updateContractor(data: ReviewContractorData): Promise<void> {
    return this.reviewContractor.updateElement(data.id, data);
  }
  // #endregion Contractor

  // #region History
  /**
   * @param {string} reviewId
   * @path /reviewHistoryRecord/$estateId/$reviewId/
   * @key id
   */
  private reviewHistory = (reviewId: string) =>
    this.dbSrv.estateFactory.getCollectionReadOnly<ReviewHistoryRecordData>(`reviewHistoryRecord/$eId/${reviewId}`);

  /**
   * @path /reviewHistoryRecord/$estateId/
   * @key reviewId
   */
  private get allReviewHistory() {
    return this.dbSrv.estateFactory.getCollectionReadOnly<Collection<ReviewHistoryRecordData>>('reviewHistoryRecord/$eId');
  }

  public getAllHistoryRecords$(): Observable<ReviewHistoryRecordData[]> {
    return this.allReviewHistory.getObject().pipe(map((obj) => ObjHlp.flatten<ReviewHistoryRecordData>(obj)));
  }

  public getHistoryRecords$(reviewId: string): Observable<ReviewHistoryRecordData[]> {
    return this.reviewHistory(reviewId).getList();
  }
  // #endregion History

  // #region Settings

  private get reviewSettings() {
    return this.dbSrv.estateFactory.getObject<EstateReviewSettings>('reviewSettings/estate/$eId');
  }

  public getReviewSettings$(): Observable<EstateReviewSettings | Nullish> {
    return this.reviewSettings.get();
  }
  // #endregion Settings
}
